import { ariaHideOutside, usePreventScroll } from "@react-aria/overlays";
import React, { useEffect, useId, useRef, useState } from "react";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../../styles/themes";
import { ButtonPrimary } from "../../Clickables";
import { MAIN_NAV_DATA_ATTR } from "../../HeaderNav/Header";
import { Svg } from "../../Svg";

import styles from "./Loading.module.scss";

const FADE_DURATION_MS = 500;
const FADE_DELAY_MS = 200;
export const FADE_TOTAL_MS = FADE_DURATION_MS + FADE_DELAY_MS;

/**
 * Covers the suite builder while storage hydrates and the catalog API
 * resolves, then fades out. On error, holds open with a retry.
 *
 * Non-portaled so SSR includes it (a portal-based overlay would let
 * the underlying landing flash before client hydration). Focus and AT
 * containment via `ariaHideOutside` — sibling inert, except the top
 * nav, so users can navigate away if loading hangs or errors.
 */
export const Loading = ({
    visible,
    errored = false,
    onRetry,
}: {
    visible: boolean;
    errored?: boolean;
    onRetry: () => void;
}) => {
    const [mounted, setMounted] = useState(visible || errored);
    // Re-show immediately when visible/errored flips back on. Adjusting state
    // during render (instead of in an effect) avoids an extra unmounted frame.
    if ((visible || errored) && !mounted) {
        setMounted(true);
    }
    const overlayRef = useRef<HTMLDivElement>(null);
    const id = useId();
    const headingId = `error-${id}`;
    const bodyId = `error-body-${id}`;

    usePreventScroll({ isDisabled: !visible && !errored });

    useEffect(() => {
        if (visible || errored) return;
        const timer = setTimeout(() => setMounted(false), FADE_TOTAL_MS);
        return () => clearTimeout(timer);
    }, [visible, errored]);

    useEffect(() => {
        if (!visible && !errored) return;
        if (!overlayRef.current) return;
        const nav = document.querySelector<HTMLElement>(
            `[${MAIN_NAV_DATA_ATTR}]`,
        );
        const targets: Element[] = [overlayRef.current];
        if (nav) targets.push(nav);
        return ariaHideOutside(targets, { shouldUseInert: true });
    }, [visible, errored]);

    if (!mounted) return null;

    const isFading = !visible && !errored;
    const classes = concatClassNames([
        styles.loading,
        isFading ? styles.fadingOut : "",
        errored ? styles.errored : "",
    ]);

    // Hardcoding locales and even hrefs is bad but suite builder is en-us only
    // and this state is rare, so this is what we're rolling with for now
    const contactHref = "/en-us/customer-care";

    return (
        <Themed
            theme="eggplantGradient"
            ref={overlayRef}
            className={classes}
            style={{
                "--fade-duration": `${FADE_DURATION_MS}ms`,
                "--fade-delay": `${FADE_DELAY_MS}ms`,
            }}
        >
            {errored ? (
                <div
                    role="alert"
                    aria-labelledby={headingId}
                    aria-describedby={bodyId}
                    className={styles.errorContent}
                >
                    <h2 id={headingId} className={styles.heading}>
                        {t`Sorry for the Interruption`}
                    </h2>
                    <p id={bodyId} className={styles.errorMessage}>
                        {richT(
                            t`We’re having trouble loading this information. Try refreshing the page or reloading the experience. If the issue continues, please <link>contact</link> our Customer Care team for support.`,
                            {
                                link: (children) => (
                                    <a href={contactHref}>{children}</a>
                                ),
                            },
                        )}
                    </p>
                    <ButtonPrimary onClick={onRetry} autoFocus>
                        {t`Reload Suite Builder`}
                    </ButtonPrimary>
                </div>
            ) : (
                <div role="status" aria-busy="true" aria-label={t`Loading`}>
                    <Svg
                        name="sks-logo"
                        className={styles.logo}
                        aria-hidden="true"
                    />
                </div>
            )}
        </Themed>
    );
};
